import { useState, useEffect, useCallback } from 'react';
import { Spin, notification, Modal } from 'antd';
import { BellFilled, EnvironmentOutlined, HeartOutlined, LinkOutlined, PlusOutlined, UsergroupAddOutlined, UserAddOutlined, WhatsAppOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader, useLang } from '../shared/utils';
import { CommunityTab } from './Community';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const CONTRIB_TYPES_UP = ['Monetary','Goods/Items','Service','Infrastructure','Other'];
const ORG_TYPES_UP     = ['Gurukul/Vidyapeeth','School/College','Temple/Mandir/Tirth','Hospital','NGO/Sansthan','ABRSVS Branch','Government','Orphanage/Ashram','Other'];

const STATUS_STYLE_UP = {
  Pending:  { bg: '#fffbeb', border: '#fbbf24', text: '#92400e', label: '⏳ Pending Review' },
  Approved: { bg: '#f0fdf4', border: '#4ade80', text: '#166534', label: '✅ Approved'       },
  Rejected: { bg: '#fef2f2', border: '#f87171', text: '#991b1b', label: '❌ Rejected'       },
};

export function ContributionsTab({ profile }) {
  const [list,       setList]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [formOpen,   setFormOpen]   = useState(false);
  const [editItem,   setEditItem]   = useState(null);   // null = new, object = editing rejected
  const [viewItem,   setViewItem]   = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    org_name: '', org_type: '', org_phone: '', org_city: '', org_state: '',
    contribution_type: '', amount: '', quantity_desc: '',
    contribution_date: '', purpose: '', description: '', is_public: true,
  });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/member/contributions`, { headers: authHeader() });
      if (res.data.status) setList(res.data.result || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openNew = () => {
    setEditItem(null);
    setForm({
      org_name: '', org_type: '', org_phone: '', org_city: '', org_state: '',
      contribution_type: '', amount: '', quantity_desc: '',
      contribution_date: new Date().toISOString().slice(0, 10),
      purpose: '', description: '', is_public: true,
    });
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      org_name:          item.org_name || '',
      org_type:          item.org_type || '',
      org_phone:         item.org_phone || '',
      org_city:          item.org_city || '',
      org_state:         item.org_state || '',
      contribution_type: item.contribution_type || '',
      amount:            item.amount || '',
      quantity_desc:     item.quantity_desc || '',
      contribution_date: item.contribution_date?.slice(0, 10) || '',
      purpose:           item.purpose || '',
      description:       item.description || '',
      is_public:         item.is_public !== 0,
    });
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    const UNSAFE = /<[^>]*>|javascript\s*:|on\w+\s*=|SELECT\s+|INSERT\s+|DROP\s+|DELETE\s+|UPDATE\s+|UNION\s+/i;
    const warn = (msg) => { notification.warning({ message: msg, placement: 'top', duration: 4 }); };

    // ── Org name ───────────────────────────────────────────────────────────
    const orgName = form.org_name?.trim();
    if (!orgName)              { warn('संस्था / संगठन का नाम दर्ज करें।'); return; }
    if (orgName.length < 2)   { warn('संस्था का नाम कम से कम 2 अक्षर होना चाहिए।'); return; }
    if (orgName.length > 255) { warn('संस्था का नाम बहुत लंबा है।'); return; }
    if (UNSAFE.test(orgName)) { warn('संस्था के नाम में अमान्य वर्ण हैं।'); return; }

    // ── Org type ───────────────────────────────────────────────────────────
    if (!form.org_type)       { warn('संस्था प्रकार चुनें।'); return; }

    // ── Org phone (optional) ───────────────────────────────────────────────
    const orgPhone = form.org_phone?.trim();
    if (orgPhone) {
      if (!/^\d{10}$/.test(orgPhone))  { warn('संस्था का फोन नंबर 10 अंकों का होना चाहिए।'); return; }
      if (!/^[6-9]/.test(orgPhone))    { warn('संस्था का फोन 6, 7, 8 या 9 से शुरू होना चाहिए।'); return; }
    }

    // ── City / State (optional but no scripts) ─────────────────────────────
    if (form.org_city?.trim()  && UNSAFE.test(form.org_city))  { warn('शहर में अमान्य वर्ण हैं।'); return; }
    if (form.org_state?.trim() && UNSAFE.test(form.org_state)) { warn('राज्य में अमान्य वर्ण हैं।'); return; }

    // ── Contribution type ──────────────────────────────────────────────────
    if (!form.contribution_type) { warn('योगदान का प्रकार चुनें।'); return; }

    // ── Date ──────────────────────────────────────────────────────────────
    if (!form.contribution_date) { warn('योगदान की तारीख दर्ज करें।'); return; }
    const picked = new Date(form.contribution_date);
    const today  = new Date(); today.setHours(23, 59, 59, 0);
    if (isNaN(picked.getTime()))    { warn('तारीख अमान्य है।'); return; }
    if (picked > today)             { warn('तारीख भविष्य की नहीं हो सकती।'); return; }
    if (picked.getFullYear() < 1950){ warn('तारीख 1950 से पहले की नहीं हो सकती।'); return; }

    // ── Amount / Quantity ──────────────────────────────────────────────────
    if (form.contribution_type === 'Monetary') {
      const amt = parseFloat(form.amount);
      if (!form.amount)          { warn('राशि (₹) दर्ज करें।'); return; }
      if (isNaN(amt) || amt <= 0){ warn('राशि एक वैध धनात्मक संख्या होनी चाहिए।'); return; }
      if (amt > 99999999)        { warn('राशि अधिकतम ₹9,99,99,999 तक हो सकती है।'); return; }
    } else {
      const qty = form.quantity_desc?.trim();
      if (!qty)             { warn('वस्तु / सेवा का विवरण दर्ज करें।'); return; }
      if (qty.length < 3)   { warn('विवरण कम से कम 3 अक्षर होना चाहिए।'); return; }
      if (UNSAFE.test(qty)) { warn('वस्तु विवरण में अमान्य वर्ण हैं।'); return; }
    }

    // ── Purpose / Description (optional, no scripts) ───────────────────────
    if (form.purpose?.trim()     && UNSAFE.test(form.purpose))    { warn('उद्देश्य में अमान्य वर्ण हैं।'); return; }
    if (form.description?.trim() && UNSAFE.test(form.description)){ warn('विवरण में अमान्य वर्ण हैं।'); return; }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        submitter_name:  profile?.name_hn || profile?.name_eng || profile?.name || '',
        submitter_phone: profile?.phone    || profile?.phone_no || '',
      };
      let res;
      if (editItem) {
        res = await axios.patch(`${baseUrl}/member/contributions/${editItem.id}`, payload, { headers: authHeader() });
      } else {
        res = await axios.post(`${baseUrl}/member/contributions`, payload, { headers: authHeader() });
      }
      if (res.data.status) {
        notification.success({ message: editItem ? 'Resubmitted for review ✅' : 'जमा हो गया ✅', placement: 'top' });
        setFormOpen(false);
        fetchList();
      } else {
        notification.error({ message: res.data.message || 'Error', placement: 'top' });
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Server error', placement: 'top' });
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('क्या आप इसे हटाना चाहते हैं?')) return;
    try {
      await axios.delete(`${baseUrl}/member/contributions/${item.id}`, { headers: authHeader() });
      notification.success({ message: 'Deleted', placement: 'top' });
      fetchList();
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'top' });
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtAmt  = (r) => r.contribution_type === 'Monetary' && r.amount
    ? `₹${Number(r.amount).toLocaleString('en-IN')}`
    : (r.quantity_desc || '—');

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4, display: 'block' };
  const rowStyle   = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };

  return (
    <div style={{ padding: '16px 0' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, padding: '0 16px' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: M }}>🤝 सामाजिक योगदान</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>किसी भी संस्था / संगठन में आपके योगदान की जानकारी</div>
        </div>
        <button onClick={openNew} style={{
          background: M, color: '#fff', border: 'none', borderRadius: 10,
          padding: '9px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <PlusOutlined /> Add Contribution
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
      ) : list.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 16px' }}>
          <HeartOutlined style={{ fontSize: 40, color: '#d1d5db', marginBottom: 12 }} />
          <div style={{ fontSize: 14, color: '#9ca3af' }}>अभी कोई योगदान नहीं जोड़ा गया</div>
          <button onClick={openNew} style={{ marginTop: 14, background: M, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            पहला योगदान जोड़ें
          </button>
        </div>
      ) : (
        <div style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {list.map(item => {
            const s = STATUS_STYLE_UP[item.status] || STATUS_STYLE_UP.Pending;
            return (
              <div key={item.id} style={{ background: '#fff', borderRadius: 12, border: item.org_type === 'ABRSVS Branch' ? `2px solid ${M}` : `1.5px solid ${s.border}`, overflow: 'hidden' }}>
                <div style={{ background: item.org_type === 'ABRSVS Branch' ? M : s.bg, padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: item.org_type === 'ABRSVS Branch' ? G : s.text }}>{s.label}</span>
                    {item.org_type === 'ABRSVS Branch' && (
                      <span style={{ background: G, color: M, fontSize: 10, fontWeight: 900, borderRadius: 4, padding: '1px 6px', letterSpacing: 0.5 }}>
                        ABRSVS
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: item.org_type === 'ABRSVS Branch' ? 'rgba(255,215,0,0.7)' : '#9ca3af' }}>{fmtDate(item.created_at)}</span>
                </div>
                <div style={{ padding: '12px 14px' }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#111', marginBottom: 4 }}>{item.org_name}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: item.purpose ? 6 : 0 }}>
                    <span style={{ background: item.org_type === 'ABRSVS Branch' ? '#fff0f0' : '#f3f4f6', borderRadius: 6, padding: '2px 8px', fontSize: 11, color: item.org_type === 'ABRSVS Branch' ? M : '#374151', fontWeight: item.org_type === 'ABRSVS Branch' ? 700 : 400 }}>{item.org_type}</span>
                    <span style={{ background: '#eff6ff', borderRadius: 6, padding: '2px 8px', fontSize: 11, color: '#1d4ed8', fontWeight: 700 }}>{item.contribution_type}</span>
                    <span style={{ background: '#f0fdf4', borderRadius: 6, padding: '2px 8px', fontSize: 11, color: '#166534', fontWeight: 700 }}>{fmtAmt(item)}</span>
                    <span style={{ background: '#fdf4ff', borderRadius: 6, padding: '2px 8px', fontSize: 11, color: '#7e22ce' }}>{fmtDate(item.contribution_date)}</span>
                  </div>
                  {item.purpose && <div style={{ fontSize: 12, color: '#6b7280' }}>{item.purpose}</div>}

                  {/* Rejection reason */}
                  {item.status === 'Rejected' && item.admin_notes && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '6px 10px', marginTop: 8, fontSize: 12, color: '#991b1b' }}>
                      <b>Rejected:</b> {item.admin_notes}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button onClick={() => setViewItem(item)} style={{ flex: 1, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 0', fontSize: 12, cursor: 'pointer', fontWeight: 600, color: '#374151' }}>
                      View Details
                    </button>
                    {item.status === 'Rejected' && (
                      <button onClick={() => openEdit(item)} style={{ flex: 1, background: M, border: 'none', borderRadius: 7, padding: '7px 0', fontSize: 12, cursor: 'pointer', fontWeight: 700, color: '#fff' }}>
                        Update & Resubmit
                      </button>
                    )}
                    {item.status !== 'Approved' && (
                      <button onClick={() => handleDelete(item)} style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 7, padding: '7px 10px', fontSize: 12, cursor: 'pointer', color: '#dc2626' }}>
                        🗑
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add/Edit Form Modal ─────────────────────────────────────────── */}
      {formOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => e.target === e.currentTarget && setFormOpen(false)}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 540, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>

            {/* ── Colored header (Community Board style) ── */}
            <div style={{ background: `linear-gradient(135deg, ${M} 0%, #b71c1c 100%)`, padding: '18px 20px 16px', flexShrink: 0, position: 'relative' }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: '#fff' }}>
                {editItem ? '✏️ Update & Resubmit' : '🤝 नया योगदान जोड़ें'}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 3 }}>
                सामाजिक योगदान — Samajik Yogdan
              </div>
              <button onClick={() => setFormOpen(false)} style={{
                position: 'absolute', top: 14, right: 14,
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)', border: 'none',
                color: '#fff', fontSize: 16, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700,
              }}>×</button>
            </div>

            {/* ── Scrollable body ── */}
            <div style={{ overflowY: 'auto', padding: '20px 20px 8px', flex: 1 }}>

              {/* Organization section */}
              <div style={{ fontSize: 11, fontWeight: 700, color: M, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>संस्था / संगठन की जानकारी</div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>संस्था / संगठन का नाम *</label>
                <input style={inputStyle} value={form.org_name} onChange={e => setForm(f => ({ ...f, org_name: e.target.value }))} placeholder="" maxLength={255} />
              </div>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>संस्था प्रकार *</label>
                  <select style={inputStyle} value={form.org_type} onChange={e => setForm(f => ({ ...f, org_type: e.target.value }))}>
                    <option value="">चुनें...</option>
                    {ORG_TYPES_UP.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>संस्था का फोन नंबर</label>
                  <input style={inputStyle} value={form.org_phone} onChange={e => setForm(f => ({ ...f, org_phone: e.target.value }))} placeholder="" maxLength={15} />
                </div>
                <div>
                  <label style={labelStyle}>शहर</label>
                  <input style={inputStyle} value={form.org_city} onChange={e => setForm(f => ({ ...f, org_city: e.target.value }))} placeholder="" maxLength={100} />
                </div>
                <div>
                  <label style={labelStyle}>राज्य</label>
                  <input style={inputStyle} value={form.org_state} onChange={e => setForm(f => ({ ...f, org_state: e.target.value }))} placeholder="" maxLength={100} />
                </div>
              </div>

              {/* Contribution section */}
              <div style={{ fontSize: 11, fontWeight: 700, color: M, textTransform: 'uppercase', letterSpacing: 1, margin: '16px 0 10px' }}>योगदान का विवरण</div>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>योगदान का प्रकार *</label>
                  <select style={inputStyle} value={form.contribution_type} onChange={e => setForm(f => ({ ...f, contribution_type: e.target.value }))}>
                    <option value="">चुनें...</option>
                    {CONTRIB_TYPES_UP.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>तारीख *</label>
                  <input type="date" style={inputStyle} value={form.contribution_date} onChange={e => setForm(f => ({ ...f, contribution_date: e.target.value }))} />
                </div>
              </div>

              {form.contribution_type === 'Monetary' && (
                <div style={{ marginBottom: 12, marginTop: 12 }}>
                  <label style={labelStyle}>राशि (₹) *</label>
                  <input type="number" style={inputStyle} value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="" min="1" />
                </div>
              )}
              {form.contribution_type && form.contribution_type !== 'Monetary' && (
                <div style={{ marginBottom: 12, marginTop: 12 }}>
                  <label style={labelStyle}>वस्तु / सेवा का विवरण *</label>
                  <input style={inputStyle} value={form.quantity_desc} onChange={e => setForm(f => ({ ...f, quantity_desc: e.target.value }))} placeholder="" maxLength={500} />
                </div>
              )}

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>उद्देश्य / कारण</label>
                <input style={inputStyle} value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} placeholder="" maxLength={500} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>विस्तृत विवरण (optional)</label>
                <textarea style={{ ...inputStyle, height: 80, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="" maxLength={2000} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 4 }}>
                <input type="checkbox" id="is_public" checked={form.is_public} onChange={e => setForm(f => ({ ...f, is_public: e.target.checked }))} style={{ width: 15, height: 15, cursor: 'pointer' }} />
                <label htmlFor="is_public" style={{ fontSize: 12, color: '#374151', cursor: 'pointer' }}>
                  ℹ️ इसे मेरी प्रोफाइल पर सार्वजनिक रूप से दिखाएं
                </label>
              </div>
            </div>

            {/* ── Fixed footer with cancel + submit ── */}
            <div style={{ padding: '14px 20px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 10, flexShrink: 0 }}>
              <button onClick={() => setFormOpen(false)}
                style={{ flex: 1, background: '#fff', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: 10, padding: '12px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                रद्द करें
              </button>
              <button onClick={handleSubmit} disabled={submitting}
                style={{ flex: 2, background: submitting ? '#9ca3af' : M, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontWeight: 800, fontSize: 14, cursor: submitting ? 'not-allowed' : 'pointer' }}>
                {submitting ? 'जमा हो रहा है...' : editItem ? '✏️ Update & Resubmit करें' : '🚀 योगदान जमा करें'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Detail Modal ───────────────────────────────────────────── */}
      {viewItem && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => e.target === e.currentTarget && setViewItem(null)}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 540, maxHeight: '88vh', overflowY: 'auto', padding: '24px 24px 32px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: M }}>योगदान विवरण</div>
              <button onClick={() => setViewItem(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9ca3af' }}>✕</button>
            </div>
            {(() => {
              const s = STATUS_STYLE_UP[viewItem.status] || STATUS_STYLE_UP.Pending;
              const fmtAmt2 = viewItem.contribution_type === 'Monetary' && viewItem.amount ? `₹${Number(viewItem.amount).toLocaleString('en-IN')}` : viewItem.quantity_desc || '—';
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, color: s.text }}>{s.label}</div>
                  <VRow label="संस्था / संगठन" value={viewItem.org_name} />
                  <VRow label="प्रकार" value={viewItem.org_type} />
                  {viewItem.org_phone && <VRow label="संस्था फोन" value={viewItem.org_phone} />}
                  {(viewItem.org_city || viewItem.org_state) && <VRow label="स्थान" value={[viewItem.org_city, viewItem.org_state].filter(Boolean).join(', ')} />}
                  <VRow label="योगदान का प्रकार" value={viewItem.contribution_type} />
                  <VRow label="राशि / वस्तु" value={fmtAmt2} />
                  <VRow label="तारीख" value={new Date(viewItem.contribution_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })} />
                  {viewItem.purpose && <VRow label="उद्देश्य" value={viewItem.purpose} />}
                  {viewItem.description && <VRow label="विवरण" value={viewItem.description} />}
                  {viewItem.status === 'Rejected' && viewItem.admin_notes && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', marginBottom: 2 }}>REJECTION REASON</div>
                      <div style={{ fontSize: 13, color: '#7f1d1d' }}>{viewItem.admin_notes}</div>
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>जमा किया: {new Date(viewItem.created_at).toLocaleDateString('en-IN')}</div>
                  {viewItem.status === 'Rejected' && (
                    <button onClick={() => { setViewItem(null); openEdit(viewItem); }}
                      style={{ background: M, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer', marginTop: 8 }}>
                      Update & Resubmit करें
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Referral Tab ─────────────────────────────────────────────────────────────
export function VRow({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
      <span style={{ fontSize: 13, color: '#111', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

export function ReferralTab({ profile }) {
  const [referrals,    setReferrals]    = useState([]);
  const [loading,      setLoading]      = useState(true);

  // referral-requests state
  const [incoming,     setIncoming]     = useState([]);  // requests I need to respond to
  const [outgoing,     setOutgoing]     = useState([]);  // requests I sent
  const [myReferrer,   setMyReferrer]   = useState(null);
  const [responding,   setResponding]   = useState({});  // { [id]: true }

  // send-request state
  const [searchQ,      setSearchQ]      = useState('');
  const [searchRes,    setSearchRes]    = useState([]);
  const [searching,    setSearching]    = useState(false);
  const [sendingReq,   setSendingReq]   = useState(false);
  const [reqNote,      setReqNote]      = useState('');
  const [selectedMbr,  setSelectedMbr]  = useState(null);
  const srchDebRef = useState(null);

  // apply-code state (existing)
  const [applyCode,    setApplyCode]    = useState('');
  const [applyStatus,  setApplyStatus]  = useState(null);
  const [applyMsg,     setApplyMsg]     = useState('');
  const debRef = useState(null);

  const hasReferrer = !!myReferrer || profile?.has_referrer;

  const loadAll = () => {
    Promise.all([
      axios.get(`${baseUrl}/member/my-referrals`,      { headers: authHeader() }),
      axios.get(`${baseUrl}/member/referral-requests`, { headers: authHeader() }),
    ]).then(([r, rr]) => {
      if (r.data.status)  setReferrals(r.data.result || []);
      if (rr.data.status) {
        setIncoming(rr.data.incoming || []);
        setOutgoing(rr.data.outgoing || []);
        setMyReferrer(rr.data.my_referrer || null);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  // ── Member search for sending request ─────────────────────────────────────
  const searchMembers = (val) => {
    setSearchQ(val); setSelectedMbr(null);
    if (srchDebRef[0]) clearTimeout(srchDebRef[0]);
    if (!val.trim()) { setSearchRes([]); return; }
    setSearching(true);
    srchDebRef[0] = setTimeout(async () => {
      try {
        const r = await axios.get(`${baseUrl}/member/member-search?search=${encodeURIComponent(val)}&limit=8`, { headers: authHeader() });
        setSearchRes((r.data.result || []).filter(m => m.id !== profile?.id));
      } catch {} finally { setSearching(false); }
    }, 400);
  };

  const sendRequest = async () => {
    if (!selectedMbr) return;
    setSendingReq(true);
    try {
      const res = await axios.post(`${baseUrl}/member/referral-request`, { to_user_id: selectedMbr.id, message: reqNote }, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: res.data.message, placement: 'topRight' });
        setSelectedMbr(null); setSearchQ(''); setSearchRes([]); setReqNote('');
        loadAll();
      }
    } catch (err) { notification.error({ message: err.response?.data?.message || 'कुछ समस्या हुई', placement: 'topRight' }); }
    finally { setSendingReq(false); }
  };

  // ── Respond to incoming request ───────────────────────────────────────────
  const respond = async (id, action) => {
    setResponding(s => ({ ...s, [id]: true }));
    try {
      const res = await axios.patch(`${baseUrl}/member/referral-request/${id}`, { action }, { headers: authHeader() });
      if (res.data.status) { notification.success({ message: res.data.message, placement: 'topRight' }); loadAll(); }
    } catch (err) { notification.error({ message: err.response?.data?.message || 'Error', placement: 'topRight' }); }
    finally { setResponding(s => ({ ...s, [id]: false })); }
  };

  const refCode = profile?.membership_no || (profile?.id ? `ABRSVS#${profile.id}` : null);

  const copyRefCode = () => {
    if (!refCode) return;
    navigator.clipboard?.writeText(refCode).then(() => {
      notification.success({ message: 'रेफरल कोड कॉपी हो गया!', placement: 'topRight', duration: 2 });
    }).catch(() => {
      notification.info({ message: `आपका कोड: ${refCode}`, placement: 'topRight', duration: 4 });
    });
  };

  const shareReferral = () => {
    const senderName = profile?.name_hn || profile?.name || 'एक सदस्य';
    const portalUrl  = 'https://portal.brahmdhamtirth.org/user-portal-login';
    const codeBlock  = refCode
      ? `\n\n🔑 *आपका रेफरल कोड:*\n*${refCode}*\n_( पंजीकरण करते समय यह कोड जरूर डालें )_`
      : '';

    const msg =
`🙏 *जय गुरूमहाराज री सा* 🙏

नमस्ते!

*${senderName}* जी ने आपको *अखिल भारतीय राजपुरोहित समाज विकास संस्थान (ABRSVS)* से जोड़ने के लिए आमंत्रित किया है।

━━━━━━━━━━━━━━━━
🏛️ *समाज शिक्षा निधि समर्पण योजना*
━━━━━━━━━━━━━━━━
समाज के बच्चों की शिक्षा व विकास के लिए हम सब मिलकर योगदान करें।${codeBlock}

✅ *सदस्यता के लाभ:*
• 📚 समाज की जानकारी एक जगह
• 💳 भुगतान व रसीद की सुविधा
• 🏘️ गाँव निर्देशिका व जनगणना
• 🤝 योगदान की पारदर्शिता

📲 *अभी जुड़ें:*
${portalUrl}

_"नया सदस्य बनें" पर क्लिक करें → फॉर्म भरें → रेफरल कोड डालें_`;

    if (navigator.share) {
      navigator.share({ title: 'ABRSVS — समाज शिक्षा निधि समर्पण योजना', text: msg }).catch(() => {
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    }
  };

  const onApplyCodeChange = (val) => {
    setApplyCode(val);
    setApplyStatus(null);
    setApplyMsg('');
    if (debRef[0]) clearTimeout(debRef[0]);
    if (!val.trim()) return;
    setApplyStatus('checking');
    debRef[0] = setTimeout(async () => {
      try {
        const r = await axios.get(`${baseUrl}/member/validate-ref-code?code=${encodeURIComponent(val.trim())}`, { headers: authHeader() });
        if (r.data.status) { setApplyStatus('ok'); setApplyMsg(r.data.referrer_name); }
        else { setApplyStatus('error'); setApplyMsg('कोड नहीं मिला'); }
      } catch { setApplyStatus('error'); setApplyMsg('कोड नहीं मिला'); }
    }, 600);
  };

  const submitReferral = async () => {
    if (!applyCode.trim() || applyStatus !== 'ok') return;
    try {
      const r = await axios.post(`${baseUrl}/member/apply-referral`, { code: applyCode.trim() }, { headers: authHeader() });
      if (r.data.status) {
        notification.success({ message: r.data.message, placement: 'topRight' });
        setApplyCode('');
        setApplyStatus(null);
        loadAll();
      } else {
        notification.error({ message: r.data.message, placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'कुछ समस्या हुई', placement: 'topRight' });
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;

  const MemberCard = ({ m, badge, badgeColor = '#15803d', extra }) => {
    const name = m.name || 'ABRSVS Member';
    const loc  = [m.village, m.district].filter(Boolean).join(' • ');
    return (
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
        {m.photo
          ? <img src={m.photo} alt={name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          : <div style={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg,${M},#b71c1c)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 900, color: G, flexShrink: 0 }}>{name[0].toUpperCase()}</div>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{name}</div>
          {loc && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}><EnvironmentOutlined style={{ marginRight: 3, fontSize: 10 }} />{loc}</div>}
          {m.membership_no && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>#{m.membership_no}</div>}
        </div>
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          {badge && <div style={{ fontSize: 10, background: `${badgeColor}18`, color: badgeColor, borderRadius: 20, padding: '2px 8px', fontWeight: 700, marginBottom: 4 }}>{badge}</div>}
          {extra}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* ── मुझे किसने जोड़ा (Who referred me) ── */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, position: 'relative', zIndex: 10 }}>
        <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '10px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}><UserAddOutlined style={{ color: M, marginRight: 6 }} />मुझे किसने जोड़ा</div>
        </div>
        <div style={{ padding: 14 }}>
          {myReferrer ? (
            <MemberCard m={myReferrer} badge="आपके रेफरर" badgeColor={M} />
          ) : outgoing.length > 0 ? (
            <div>
              {outgoing.map(o => (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#6b7280', flexShrink: 0 }}>{o.name[0]}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{o.name}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>अनुरोध भेजा</div>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                    background: o.status === 'pending' ? '#fef3c7' : o.status === 'accepted' ? '#dcfce7' : '#fee2e2',
                    color: o.status === 'pending' ? '#92400e' : o.status === 'accepted' ? '#15803d' : '#991b1b' }}>
                    {o.status === 'pending' ? 'प्रतीक्षारत' : o.status === 'accepted' ? '✓ स्वीकृत' : '✗ अस्वीकृत'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', marginBottom: 12 }}>अभी कोई रेफरर नहीं जुड़ा</div>
          )}

          {/* Send referral claim request */}
          {!myReferrer && (
            <div style={{ marginTop: myReferrer || outgoing.length ? 12 : 0, paddingTop: outgoing.length ? 12 : 0, borderTop: outgoing.length ? '1px solid #f3f4f6' : 'none' }}>

              {/* ── Option A: Enter referral code directly ── */}
              <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: M, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                  🔑 रेफरल कोड से जोड़ें
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      value={applyCode}
                      onChange={e => onApplyCodeChange(e.target.value.toUpperCase())}
                      placeholder="रेफरल कोड डालें (जैसे: ABC123)"
                      maxLength={20}
                      style={{
                        width: '100%', padding: '9px 12px', border: `1.5px solid ${
                          applyStatus === 'ok' ? '#16a34a' : applyStatus === 'error' ? '#dc2626' : '#d1d5db'
                        }`, borderRadius: 8, fontSize: 14, fontFamily: 'monospace', fontWeight: 700,
                        letterSpacing: 1.5, outline: 'none', boxSizing: 'border-box',
                        color: applyStatus === 'ok' ? '#16a34a' : applyStatus === 'error' ? '#dc2626' : '#111',
                        textTransform: 'uppercase',
                      }}
                    />
                    {applyStatus === 'checking' && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>🔍 कोड जाँच रहे हैं…</div>
                    )}
                    {applyStatus === 'ok' && applyMsg && (
                      <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4, fontWeight: 700 }}>
                        ✅ {applyMsg} का कोड मिला
                      </div>
                    )}
                    {applyStatus === 'error' && (
                      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>❌ यह कोड मान्य नहीं है</div>
                    )}
                  </div>
                  <button
                    onClick={submitReferral}
                    disabled={applyStatus !== 'ok'}
                    style={{
                      background: applyStatus === 'ok' ? M : '#e5e7eb',
                      color: applyStatus === 'ok' ? G : '#9ca3af',
                      border: 'none', borderRadius: 8, padding: '9px 14px',
                      fontSize: 12, fontWeight: 800, cursor: applyStatus === 'ok' ? 'pointer' : 'not-allowed',
                      flexShrink: 0, whiteSpace: 'nowrap', marginTop: 1,
                      transition: 'all 0.2s',
                    }}
                  >
                    जोड़ें →
                  </button>
                </div>
              </div>

              {/* ── Option B: Search by name ── */}
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>या नाम से खोजकर अनुरोध भेजें:</div>
              <div style={{ position: 'relative' }}>
                <input value={searchQ} onChange={e => searchMembers(e.target.value)}
                  placeholder="सदस्य का नाम खोजें…"
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                {searching && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>खोज रहे हैं…</div>}
                {searchRes.length > 0 && !selectedMbr && (
                  <div style={{ position: 'absolute', zIndex: 999, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', top: '100%', left: 0, right: 0, maxHeight: 200, overflowY: 'auto' }}>
                    {searchRes.map(m => (
                      <div key={m.id} onClick={() => { setSelectedMbr(m); setSearchQ(m.name); setSearchRes([]); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <span style={{ fontWeight: 700 }}>{m.name}</span>
                        <span style={{ color: '#9ca3af', fontSize: 11 }}>{[m.village, m.district].filter(Boolean).join(', ')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedMbr && (
                <div style={{ marginTop: 10 }}>
                  <input value={reqNote} onChange={e => setReqNote(e.target.value)}
                    placeholder="संदेश (वैकल्पिक) — कब, कैसे जोड़ा..."
                    style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 12px', fontSize: 12, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
                  <button onClick={sendRequest} disabled={sendingReq}
                    style={{ width: '100%', background: M, color: G, border: 'none', borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    {sendingReq ? 'भेज रहे हैं…' : `${selectedMbr.name} जी को अनुरोध भेजें`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Incoming referral requests ── */}
      {incoming.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #fbbf24', marginBottom: 14, overflow: 'hidden' }}>
          <div style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <BellFilled style={{ color: '#d97706', fontSize: 13 }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>रेफरल अनुरोध आए ({incoming.length})</span>
          </div>
          <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {incoming.map(req => (
              <div key={req.id}>
                <MemberCard m={req} badge={req.joining_date ? `सदस्यता: ${new Date(req.joining_date).getFullYear()}` : null} badgeColor="#6b7280"
                  extra={
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => respond(req.id, 'reject')} disabled={responding[req.id]}
                        style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        अस्वीकार
                      </button>
                      <button onClick={() => respond(req.id, 'accept')} disabled={responding[req.id]}
                        style={{ background: '#dcfce7', color: '#15803d', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        {responding[req.id] ? '…' : 'स्वीकार'}
                      </button>
                    </div>
                  }
                />
                {req.message && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, paddingLeft: 52, fontStyle: 'italic' }}>"{req.message}"</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Referral code + Share ── */}
      <div style={{ background: `linear-gradient(135deg, ${M}0d, #b71c1c0d)`, border: `1.5px solid ${M}30`, borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>आपका रेफरल कोड</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1, background: '#fff', border: `2px dashed ${M}50`, borderRadius: 8, padding: '8px 14px', fontFamily: 'monospace', fontSize: 18, fontWeight: 900, color: M, letterSpacing: 2, textAlign: 'center' }}>
            {refCode}
          </div>
          <button onClick={copyRefCode} style={{ background: M, color: G, border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
            <LinkOutlined /> कॉपी
          </button>
        </div>
        <button onClick={shareReferral} style={{ width: '100%', background: '#25d366', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 13, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <WhatsAppOutlined /> WhatsApp पर आमंत्रण भेजें
        </button>
      </div>

      {/* ── Members I referred ── */}
      {referrals.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <UsergroupAddOutlined style={{ color: M }} /> आपके द्वारा जोड़े सदस्य ({referrals.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {referrals.map((r, i) => (
              <MemberCard key={i} m={{ ...r, photo: null }}
                badge={`₹${Number(r.total_paid || 0).toLocaleString('en-IN')}`}
                badgeColor="#15803d" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Combined Contribution Tab ────────────────────────────────────────────────
export function CombinedContributionTab({ profile }) {
  const [subTab, setSubTab] = useState('abrsvs');
  const tabs = [
    { key: 'abrsvs', label: 'ABRSVS',       badge: M,        desc: 'Shiksha Nidhi — SN-ABRSVS' },
    { key: 'other',  label: 'OTHER',         badge: '#7a0000', desc: 'समाजिक योगदान / Yogdan' },
  ];
  return (
    <div>
      {/* Sub-tab switcher */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, background: '#f3f4f6', borderRadius: 12, padding: 4 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: subTab === t.key ? '#fff' : 'transparent',
            boxShadow: subTab === t.key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
            transition: 'all 0.15s',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: subTab === t.key ? '#111827' : '#6b7280' }}>{t.label}</span>
              <span style={{ background: t.badge, color: '#FFD700', fontSize: 8, fontWeight: 900, borderRadius: 3, padding: '1px 5px', letterSpacing: 0.4 }}>
                {t.key === 'abrsvs' ? 'SN-ABRSVS' : 'OTHER'}
              </span>
            </div>
            {subTab === t.key && (
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{t.desc}</div>
            )}
          </button>
        ))}
      </div>
      {subTab === 'abrsvs' && <CommunityTab profile={profile} />}
      {subTab === 'other'  && <ContributionsTab profile={profile} />}
    </div>
  );
}

